import { css, cx } from '@fable/theme'
import { FlowerHead } from '../FlowerHead'
import { FlowerPot } from '../FlowerPot'
import { FlowerLeaves } from '../FlowerLeaves'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { useRef, useState } from 'react'
import { FlowerStalk } from '../FlowerStalk'
import { FlowerProps } from 'app/webview/pages/stats_profile/lib/types'
import { getFlowerColor } from '../utils'

export const FlowerSleepy = ({
  head,
  face,
  progress,
  total,
  motion,
  startPetalAnim,
  onPetalsFilled,
}: FlowerProps) => {
  const container = useRef<HTMLDivElement>(null)
  const flowerColors = getFlowerColor(head)
  const [isViewed, setIsViewed] = useState(false)

  const fillPetals = () => {
    const tl = gsap.timeline({ onComplete: onPetalsFilled })
    const petals = gsap.utils.toArray('.anim__flower-petal')
    const result = Math.ceil((progress / total) * petals.length)
    const petalsToFill = petals.slice(0, result)

    tl.to(petalsToFill, {
      duration: 0.1,
      fill: flowerColors.filled,
      stagger: 0.15,
      ease: 'sine.inOut',
    })

    return tl
  }

  useGSAP(
    () => {
      const tl = gsap.timeline()

      if (!startPetalAnim || !isViewed) return

      tl.add(fillPetals())
    },
    { scope: container, dependencies: [startPetalAnim, isViewed] }
  )
  const appear = () => {
    const tl = gsap.timeline()

    tl.to('.anim__flower', {
      opacity: 1,
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    })

    return tl
  }

  const moveHead = () => {
    const tl = gsap.timeline({ repeat: 5, yoyo: true })

    tl.to('.anim__flower-head', {
      duration: 1.5,
      y: 3,
      ease: 'sine.inOut',
    })

    return tl
  }

  const moveLeaves = () => {
    const tl = gsap.timeline({ repeat: 5, yoyo: true, delay: 0.5 })

    tl.to('.anim__flower-leaves', {
      y: 1,
      delay: 0,
      duration: 1.5,
      ease: 'sine.inOut',
    })

    return tl
  }
  const moveZs = () => {
    const tl = gsap.timeline({ repeat: 2 })

    const zs = gsap.utils.toArray('.anim__flower-sleepy-z') as HTMLElement[]

    tl.to(zs, {
      duration: 1,
      x: 12,
      y: -15,
      scale: 1,
      ease: 'sine.in',
      stagger: { each: 0.5 },
    })

    tl.to(
      zs,
      {
        duration: 1,
        x: 24,
        y: -30,
        scale: 0,
        ease: 'sine.out',
        stagger: { each: 0.5 },
      },
      '<50%'
    )

    return tl
  }

  useGSAP(
    () => {
      const tl = gsap.timeline({
        delay: motion?.delay || 0,
        scrollTrigger: {
          trigger: container.current,
          start: '150% bottom',
          once: true,
          onToggle: (self) => !isViewed && self.isActive && setIsViewed(true),
        },
      })
      tl.add(appear(), 'start')
      tl.add(moveHead(), '<start')
      tl.add(moveLeaves(), '<start')
      tl.add(moveZs(), '<start')
    },
    { scope: container }
  )

  return (
    <div
      ref={container}
      className={css`
        width: 100%;
        height: 140px;
        position: absolute;
        top: 0;
        left: 0;
      `}
    >
      <div
        className={cx(
          'anim__flower',
          css`
            width: 100%;
            height: 100%;
            opacity: 0;
            transform: scale(0.5);
            transform-origin: bottom;
            will-change: transform, opacity;
          `
        )}
      >
        <FlowerHead
          className={css`
            top: 40%;
          `}
          type={head}
          face={face}
          colors={flowerColors}
        />
        <div
          className={css`
            width: 100%;
            height: 100%;
            position: absolute;
            left: -1px;
            bottom: 0;
          `}
        >
          <div
            className={css`
              width: 30%;
              position: absolute;
              bottom: 9px;
              left: calc(50% + 0.5px);
              transform: translateX(-50%);
            `}
          >
            <FlowerStalk position="center" />
          </div>

          <div
            className={cx(
              'anim__flower-leaves',
              css`
                width: 55px;
                position: absolute;
                bottom: 5px;
                left: 50%;
                transform: translateX(-50%);
                will-change: transform;
              `
            )}
          >
            <FlowerLeaves />
          </div>
          <FlowerPot />
        </div>
      </div>
    </div>
  )
}
